import { useEffect, useMemo, useRef, useState } from 'react';
import { GenericTable } from '../Table';
import { PaymentData, PaymentStatus, UserRole } from '../../services/api.types';
import dayjs from 'dayjs';
import { formatMoney } from '../../utils/formatters';
import {
  translatePaymentReason,
  translatePaymentStatus,
} from '../../utils/translate-enums';
import { DataAutocomplete } from '../SelectResponsibles';
import { apiService } from '../../services/api';
import clsx from 'clsx';
import { Autocomplete, TextField } from '@mui/material';
import { Select } from '../Select';
import { useAppSelector } from '../../hooks';
import { ChargeDetailsModal } from '../ChargeDetailsModal';
import { StatusLabel } from '../StatusLabel';
import { SeeBillet } from '../FinancialTabPanel';

type Payments = {
  id: number;
  Responsável: string;
  Vencimento: string;
  Valor: string;
  Status: JSX.Element;
  'Boleto / Fatura': JSX.Element;
};

interface PaymentTableProps {
  isSelectedPanel: boolean;
  responsibles: DataAutocomplete[];
  isFetchingUser: boolean;
}

export const PaymentsTable = ({
  isSelectedPanel,
  responsibles,
  isFetchingUser,
}: PaymentTableProps) => {
  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [filterOption, setFilterOption] = useState<string>('');
  const [selectedResponsible, setSelectedResponsible] =
    useState<DataAutocomplete | null>();
  const [isFetchingPayments, setIsFetchingPayments] = useState<boolean>(false);
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);

  const [selectedPaymentId, setSelectedPaymentId] = useState<number>();
  const selectedPayment = useMemo(
    () => payments.find((p) => p.id === selectedPaymentId),
    [payments, selectedPaymentId]
  );

  const clickRef = useRef(250);

  const handleChange = (value: string) => {
    setFilterOption(value);
    setSelectedPaymentId(0);
  };

  const handleSelect = (id: number) => {
    setSelectedPaymentId(id);
    handleOpen(setOpenDetailsModal);
  };

  const handleOpen = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(true);
    }, 150);
  };

  const handleClose = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(false);
    }, 100);
  };

  const handleUpdatePayment = (updatedPayment: PaymentData) => {
    setPayments((prev) =>
      prev.map((payment) =>
        payment.id === updatedPayment.id ? updatedPayment : payment
      )
    );
  };

  useEffect(() => {
    if (selectedResponsible && isSelectedPanel) {
      setIsFetchingPayments(true);
      apiService
        .getAllPaymentsByResponsible(selectedResponsible.id)
        .then((res) => {
          setPayments(res);
        })
        .finally(() => setIsFetchingPayments(false));
    } else {
      setPayments([]);
    }
  }, [selectedResponsible, isSelectedPanel]);

  useEffect(() => {
    if (user?.role === UserRole.RESPONSIBLE) {
      setSelectedResponsible(responsibles[0]);
    }
  }, [responsibles, user?.role]);

  const getPaymentSummary = (payment: Payments): string => {
    const responsible = payment.Responsável;
    let summary = responsible;
    const date = payment.Vencimento;
    summary += ` - ${date}`;
    if (filterOption === 'ALL') {
      const status = payment.Status.props.status;
      summary += ` - ${status}`;
    }
    return summary;
  };

  const rows = useMemo(() => {
    return payments
      .filter(
        (p) =>
          (filterOption === '' ||
            filterOption === 'All' ||
            p.status === filterOption) &&
          p.payerId === selectedResponsible?.id
      )
      .map((p) => ({
        id: p.id,
        Responsável: p.payerName,
        Vencimento: dayjs(p.dueDate).format('DD/MM/YYYY'),
        Valor: formatMoney(p.paidValue ?? p.value - p.discountValue),
        Motivo: translatePaymentReason(p.reason),
        Status: <StatusLabel status={translatePaymentStatus(p)} />,
        'Boleto / Fatura': (
          <SeeBillet
            link={p.bankSlipUrl ?? p.invoiceUrl ?? '#'}
            disabled={
              p.status === PaymentStatus.CANCELED ||
              (!p.bankSlipUrl && !p.invoiceUrl)
            }
          />
        ),
      }));
  }, [payments, filterOption, selectedResponsible]);

  return (
    <>
      <div className="max-h-full flex gap-6">
        <GenericTable
          rows={rows}
          getSummary={getPaymentSummary}
          isSelectable
          Footer={<div></div>}
          onSelectRow={(charge) => {
            handleSelect(charge.id);
          }}
          isFetching={isFetchingPayments}
          AboveHeader={
            <div
              className={clsx(
                'flex flex-col lg:flex-row',
                'w-full gap-4 justify-between'
              )}
            >
              {isAdminOrEmployee && (
                <div className="w-full md:w-3/5">
                  <Autocomplete
                    size="small"
                    noOptionsText={'Sem opções'}
                    value={selectedResponsible}
                    onChange={(
                      event: object,
                      newValue: DataAutocomplete | null
                    ) => {
                      setSelectedResponsible(newValue);
                    }}
                    getOptionLabel={(option) => option.displayName}
                    isOptionEqualToValue={(option, value) =>
                      option.displayName === value.displayName
                    }
                    loadingText="Carregando..."
                    loading={isFetchingUser}
                    options={responsibles}
                    renderInput={(params) => (
                      <TextField {...params} label="Selecione um Usuário" />
                    )}
                  />
                </div>
              )}
              <Select
                id="Select"
                size="small"
                label="Filtrar por"
                value={filterOption}
                onChange={handleChange}
                className={clsx('w-full md:w-3/5')}
                options={[
                  { value: 'All', label: 'Todos' },
                  { value: PaymentStatus.PAID, label: 'Pago' },
                  { value: PaymentStatus.PENDING, label: 'Pendente' },
                  { value: PaymentStatus.CANCELED, label: 'Cancelado' },
                ]}
              />
            </div>
          }
        />
      </div>
      {openDetailsModal && selectedPayment && (
        <ChargeDetailsModal
          open={openDetailsModal}
          payment={selectedPayment}
          onClose={() => handleClose(setOpenDetailsModal)}
          onUpdate={handleUpdatePayment}
        />
      )}
    </>
  );
};
